class CountdownTimer extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  interval;

  /** @type {number | undefined} */
  endDate;

  constructor() {
    super();
    this.section = this.closest('.shopify-section');
    this.countdownParent = this.closest('[data-countdown-block]') || this.section;
    this.expirationBehavior = this.getAttribute('data-expiration-behavior');
    this.time = this.querySelector('time');
    this.days = this.querySelector('[data-days]');
    this.hours = this.querySelector('[data-hours]');
    this.minutes = this.querySelector('[data-minutes]');
    this.seconds = this.querySelector('[data-seconds]');
    this.daysInMs = 86400000;
    this.hoursInMs = this.daysInMs / 24;
    this.minutesInMs = this.hoursInMs / 60;
    this.secondsInMs = this.minutesInMs / 60;
    this.shouldHideOnComplete = this.expirationBehavior === 'hide-section';
    this.shouldShowMessage = this.expirationBehavior === 'show-message';
    this.update = this.update.bind(this);
  }

  connectedCallback() {
    if (!this.time || !this.days || !this.hours || !this.minutes || !this.seconds) {
      return;
    }

    this.endDate = Date.parse(this.time.dateTime);

    if (Number.isNaN(this.endDate) || this.endDate <= Date.now()) {
      this.onComplete();
      return;
    }

    this.update();
    this.interval = setInterval(this.update, 1000);
  }

  disconnectedCallback() {
    this.stopTimer();
  }

  /** @param {number} milliseconds */
  convertTime(milliseconds) {
    const daysValue = Math.floor(milliseconds / this.daysInMs);
    milliseconds -= daysValue * this.daysInMs;
    const hoursValue = Math.floor(milliseconds / this.hoursInMs);
    milliseconds -= hoursValue * this.hoursInMs;
    const minutesValue = Math.floor(milliseconds / this.minutesInMs);
    milliseconds -= minutesValue * this.minutesInMs;

    return {
      days: this.formatDigits(daysValue),
      hours: this.formatDigits(hoursValue),
      minutes: this.formatDigits(minutesValue),
      seconds: this.formatDigits(Math.floor(milliseconds / this.secondsInMs)),
    };
  }

  /** @param {number} value */
  formatDigits(value) {
    return value < 10 ? `0${value}` : `${value}`;
  }

  /** @param {{ days: string | number; hours: string | number; minutes: string | number; seconds: string | number }} time */
  render({ days, hours, minutes, seconds }) {
    if (!this.days || !this.hours || !this.minutes || !this.seconds) return;

    this.days.textContent = String(days);
    this.hours.textContent = String(hours);
    this.minutes.textContent = String(minutes);
    this.seconds.textContent = String(seconds);
  }

  stopTimer() {
    clearInterval(this.interval);
  }

  onComplete() {
    this.render({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    if (this.shouldHideOnComplete) {
      this.countdownParent?.classList.add('hidden');
      this.countdownParent?.dispatchEvent(
        new CustomEvent('theme:countdown:hide', { detail: { element: this }, bubbles: true }),
      );
    }

    if (this.shouldShowMessage) {
      this.classList?.add('show-message');
      this.countdownParent?.dispatchEvent(new CustomEvent('theme:countdown:expire', { bubbles: true }));
    }
  }

  update() {
    if (typeof this.endDate !== 'number') return;

    const now = Date.now();
    const diff = this.endDate - now;

    if (diff < 1000) {
      this.stopTimer();
      this.onComplete();
      return;
    }

    const display = this.convertTime(diff);
    this.render(display);
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
